import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, User, Mail, Phone, Shield, Eye, EyeOff,
  Upload, FileText, CheckCircle, Check, Trash2, Download, Star, Plus,
  Instagram, Facebook, Linkedin, Twitter, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '../../contexts/SettingsContext';
import * as api from '../../services/api';
import StarRating from '../../components/StarRating';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const documentTypes = [
  { key: 'cedula', label: 'Cedula de Ciudadania', description: 'Foto de la cedula por ambos lados' },
  { key: 'rut', label: 'RUT', description: 'Registro Unico Tributario actualizado' },
  { key: 'acuerdo_referidos', label: 'Acuerdo de Referidos', description: 'Documento firmado del acuerdo' },
  { key: 'certificado_bancario', label: 'Certificado Bancario', description: 'Certificado de cuenta bancaria para pagos' },
];

const roles = [
  { id: 'admin', name: 'Administrador', color: 'bg-purple-100 text-purple-700' },
  { id: 'comercial', name: 'Comercial', color: 'bg-green-100 text-green-700' },
  { id: 'asistente', name: 'Asistente', color: 'bg-teal-100 text-teal-700' },
  { id: 'referidor', name: 'Referidor', color: 'bg-amber-100 text-amber-700' },
  { id: 'trafficker', name: 'Trafficker', color: 'bg-orange-100 text-orange-700' },
  { id: 'viewer', name: 'Solo Lectura', color: 'bg-slate-100 text-slate-700' },
];

export default function UserEdit() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const primaryColor = settings?.primaryColor || '#00b30c';
  const isNew = userId === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [displayPassword, setDisplayPassword] = useState('');
  const passwordTimeoutRef = useRef(null);

  // Referidor documents
  const [documents, setDocuments] = useState({});
  const [uploading, setUploading] = useState({});
  
  // Contact info for referidores
  const [contactInfo, setContactInfo] = useState({
    phones: [],
    emails: [],
    socialMedia: {}
  });
  
  // Lead tags for asistente role
  const [availableTags, setAvailableTags] = useState([]);
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const socialMediaTypes = [
    { key: 'instagram', label: 'Instagram', icon: Instagram },
    { key: 'facebook', label: 'Facebook', icon: Facebook },
    { key: 'linkedin', label: 'LinkedIn', icon: Linkedin },
    { key: 'twitter', label: 'X (Twitter)', icon: Twitter },
    { key: 'tiktok', label: 'TikTok', icon: Globe },
    { key: 'website', label: 'Sitio Web', icon: Globe }
  ];

  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '',
    role: 'comercial', status: 'active', avatar: '',
    commissionPercentage: 7,
    referidorRating: 0,
    assignedTags: [],
    permissions: {
      viewLeads: true, editLeads: true, deleteLeads: false,
      viewDeals: true, editDeals: true, viewStatistics: true,
      viewAllUsers: false, manageUsers: false, manageSettings: false,
    },
  });

  useEffect(() => {
    if (!isNew) loadUser();
    loadAvailableTags();
  }, [userId]);

  const loadAvailableTags = async () => {
    try {
      const tags = await api.getLeadTags();
      setAvailableTags(tags);
    } catch (err) { console.error('Error loading tags:', err); }
  };

  const loadUser = async () => {
    try {
      setLoading(true);
      const users = await api.getUsers();
      const found = users.find(u => u.id === userId);
      if (!found) {
        toast.error('Usuario no encontrado');
        navigate('/admin/users');
        return;
      }
      setUser(found);
      setFormData({
        name: found.name || '',
        email: found.email || '',
        phone: found.phone || '',
        password: '',
        role: found.role || 'comercial',
        status: found.status || 'active',
        avatar: found.avatar || '',
        commissionPercentage: found.commissionPercentage || 7,
        referidorRating: found.referidorRating || 0,
        assignedTags: found.assignedTags || [],
        permissions: found.permissions || {
          viewLeads: true, editLeads: true, deleteLeads: false,
          viewDeals: true, editDeals: true, viewStatistics: true,
          viewAllUsers: false, manageUsers: false, manageSettings: false,
        },
      });
      // Load referidor data
      if (found.role === 'referidor') {
        setDocuments(found.referidorDocuments || {});
        setContactInfo({
          phones: found.phones || [],
          emails: found.emails || [],
          socialMedia: found.socialMedia || {}
        });
      }
    } catch (error) {
      toast.error('Error al cargar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (role) => {
    let permissions = { ...formData.permissions };
    if (role === 'admin') {
      permissions = { viewLeads: true, editLeads: true, deleteLeads: true, viewDeals: true, editDeals: true, viewStatistics: true, viewAllUsers: true, manageUsers: true, manageSettings: true };
    } else if (role === 'comercial') {
      permissions = { viewLeads: true, editLeads: true, deleteLeads: false, viewDeals: true, editDeals: true, viewStatistics: true, viewAllUsers: false, manageUsers: false, manageSettings: false };
    } else if (role === 'viewer') {
      permissions = { viewLeads: true, editLeads: false, deleteLeads: false, viewDeals: true, editDeals: false, viewStatistics: true, viewAllUsers: false, manageUsers: false, manageSettings: false };
    } else if (role === 'asistente') {
      permissions = { viewLeads: true, editLeads: true, deleteLeads: false, viewDeals: true, editDeals: true, viewStatistics: true, viewAllUsers: false, manageUsers: false, manageSettings: false };
    } else {
      permissions = { viewLeads: true, editLeads: false, deleteLeads: false, viewDeals: false, editDeals: false, viewStatistics: false, viewAllUsers: false, manageUsers: false, manageSettings: false };
    }
    setFormData({ ...formData, role, permissions });
  };

  const handlePasswordChange = (e) => {
    const newValue = e.target.value;
    setFormData({ ...formData, password: newValue });
    if (passwordTimeoutRef.current) clearTimeout(passwordTimeoutRef.current);
    if (newValue.length > 0) {
      setDisplayPassword('\u2022'.repeat(Math.max(0, newValue.length - 1)) + newValue.slice(-1));
      passwordTimeoutRef.current = setTimeout(() => setDisplayPassword('\u2022'.repeat(newValue.length)), 800);
    } else {
      setDisplayPassword('');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return toast.error('El nombre es requerido');
    if (!formData.email.trim()) return toast.error('El email es requerido');

    setSaving(true);
    try {
      // Include contact info for referidores
      const dataToSave = {
        ...formData,
        phones: contactInfo.phones,
        emails: contactInfo.emails,
        socialMedia: contactInfo.socialMedia,
        referidorRating: formData.referidorRating
      };
      
      if (isNew) {
        await api.createUser(dataToSave);
        toast.success('Usuario creado');
      } else {
        await api.updateUser(userId, dataToSave);
        toast.success('Usuario actualizado');
      }
      navigate('/admin/users');
    } catch (error) {
      toast.error('Error al guardar usuario');
    } finally {
      setSaving(false);
    }
  };

  // Contact info handlers
  const addPhone = () => {
    if (!newPhone.trim()) return;
    if (contactInfo.phones.includes(newPhone.trim())) {
      toast.error('Este teléfono ya existe');
      return;
    }
    setContactInfo(prev => ({
      ...prev,
      phones: [...prev.phones, newPhone.trim()]
    }));
    setNewPhone('');
  };

  const removePhone = (phone) => {
    setContactInfo(prev => ({
      ...prev,
      phones: prev.phones.filter(p => p !== phone)
    }));
  };

  const addEmail = () => {
    if (!newEmail.trim()) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      toast.error('Email inválido');
      return;
    }
    if (contactInfo.emails.includes(newEmail.trim().toLowerCase())) {
      toast.error('Este email ya existe');
      return;
    }
    setContactInfo(prev => ({
      ...prev,
      emails: [...prev.emails, newEmail.trim().toLowerCase()]
    }));
    setNewEmail('');
  };

  const removeEmail = (email) => {
    setContactInfo(prev => ({
      ...prev,
      emails: prev.emails.filter(e => e !== email)
    }));
  };

  const updateSocialMedia = (key, value) => {
    setContactInfo(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [key]: value
      }
    }));
  };

  const handleRatingUpdate = async (rating) => {
    try {
      await api.updateReferidorRating(userId, rating);
      setUser(prev => ({ ...prev, referidorRating: rating }));
      toast.success('Calificacion actualizada');
    } catch (error) {
      toast.error('Error al actualizar calificacion');
    }
  };

  const handleDocumentUpload = async (docTypeKey, file) => {
    setUploading(prev => ({ ...prev, [docTypeKey]: true }));
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64Data = reader.result.split(',')[1];
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_URL}/api/referidos/admin-upload-document`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              documentType: docTypeKey,
              fileName: file.name,
              fileData: base64Data,
              fileType: file.type,
              userId: userId
            })
          });
          
          if (!res.ok) throw new Error('Upload failed');
          const result = await res.json();
          
          setDocuments(prev => ({
            ...prev,
            [docTypeKey]: { fileName: file.name, uploadedAt: new Date().toISOString(), ...(result.document || {}) }
          }));
          toast.success('Documento subido');
        } catch (error) {
          toast.error('Error al subir documento');
        } finally {
          setUploading(prev => ({ ...prev, [docTypeKey]: false }));
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Error al leer archivo');
      setUploading(prev => ({ ...prev, [docTypeKey]: false }));
    }
  };

  const handleDocumentDelete = async (docTypeKey) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/referidos/admin-delete-document/${userId}/${docTypeKey}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Delete failed');
      
      setDocuments(prev => {
        const next = { ...prev };
        delete next[docTypeKey];
        return next;
      });
      toast.success('Documento eliminado');
    } catch (error) {
      toast.error('Error al eliminar documento');
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="user-edit-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/admin/users')} className="p-2 hover:bg-slate-100 rounded-lg" data-testid="back-to-users">
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">
            {isNew ? 'Nuevo Usuario' : `Editar: ${user?.name || ''}`}
          </h1>
          <p className="text-sm text-slate-500">
            {isNew ? 'Crea un nuevo usuario en el sistema' : 'Modifica la informacion del usuario'}
          </p>
        </div>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 px-5 py-2.5 text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
          style={{ backgroundColor: primaryColor }}
          data-testid="save-user-btn"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Guardando...' : isNew ? 'Crear Usuario' : 'Guardar Cambios'}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info Card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-5 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <User className="w-4 h-4 text-slate-500" /> Informacion Personal
              </h2>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Juan Perez"
                  data-testid="user-name-input"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Mail className="w-3.5 h-3.5 inline mr-1" /> Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="juan@empresa.com"
                    data-testid="user-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    <Phone className="w-3.5 h-3.5 inline mr-1" /> Telefono
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-testid="user-phone-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  {!isNew ? 'Nueva Contrasena (dejar vacio para no cambiar)' : 'Contrasena *'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'text'}
                    value={showPassword ? formData.password : displayPassword}
                    onChange={handlePasswordChange}
                    className="w-full px-4 py-2.5 pr-12 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder={!isNew ? 'Ingresa nueva contrasena' : 'Minimo 6 caracteres'}
                    style={{ fontFamily: showPassword ? 'inherit' : 'monospace' }}
                    data-testid="user-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions Card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-5 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-500" /> Permisos
              </h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: 'viewLeads', label: 'Ver Leads' },
                  { key: 'editLeads', label: 'Editar Leads' },
                  { key: 'deleteLeads', label: 'Eliminar Leads' },
                  { key: 'viewDeals', label: 'Ver Negocios' },
                  { key: 'editDeals', label: 'Editar Negocios' },
                  { key: 'viewStatistics', label: 'Ver Estadisticas' },
                  { key: 'viewAllUsers', label: 'Ver Usuarios' },
                  { key: 'manageUsers', label: 'Gestionar Usuarios' },
                  { key: 'manageSettings', label: 'Gestionar Config.' },
                ].map(perm => (
                  <label key={perm.key} className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={formData.permissions[perm.key]}
                      onChange={(e) => setFormData({
                        ...formData,
                        permissions: { ...formData.permissions, [perm.key]: e.target.checked }
                      })}
                      className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <span className="text-sm text-slate-700">{perm.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Referidor Contact Info */}
          {formData.role === 'referidor' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="referidor-contact-info">
              <div className="p-5 border-b border-slate-100">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-500" /> Información de Contacto
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Teléfonos, correos y redes sociales adicionales
                </p>
              </div>
              <div className="p-5 space-y-6">
                {/* Phones */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Teléfonos Adicionales</label>
                  <div className="space-y-2 mb-2">
                    {contactInfo.phones.map((phone, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <Phone className="w-4 h-4 text-slate-400" />
                        <span className="flex-1 text-sm">{phone}</span>
                        <button onClick={() => removePhone(phone)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="+57 300 123 4567"
                      value={newPhone}
                      onChange={(e) => setNewPhone(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addPhone()}
                      className="flex-1"
                    />
                    <Button variant="outline" onClick={addPhone} size="sm">
                      <Plus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                  </div>
                </div>

                {/* Emails */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Correos Adicionales</label>
                  <div className="space-y-2 mb-2">
                    {contactInfo.emails.map((email, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <Mail className="w-4 h-4 text-slate-400" />
                        <span className="flex-1 text-sm">{email}</span>
                        <button onClick={() => removeEmail(email)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="correo@ejemplo.com"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addEmail()}
                      className="flex-1"
                    />
                    <Button variant="outline" onClick={addEmail} size="sm">
                      <Plus className="w-4 h-4 mr-1" /> Agregar
                    </Button>
                  </div>
                </div>

                {/* Social Media */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">Redes Sociales</label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {socialMediaTypes.map((social) => {
                      const Icon = social.icon;
                      return (
                        <div key={social.key} className="flex items-center gap-2">
                          <div className="p-2 bg-slate-100 rounded-lg">
                            <Icon className="w-4 h-4 text-slate-500" />
                          </div>
                          <Input
                            placeholder={social.label}
                            value={contactInfo.socialMedia[social.key] || ''}
                            onChange={(e) => updateSocialMedia(social.key, e.target.value)}
                            className="flex-1"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Referidor Documents */}
          {formData.role === 'referidor' && !isNew && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="referidor-contract-status">
              <div className="p-5 border-b border-slate-100">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" /> Estado del Contrato
                </h2>
              </div>
              <div className="p-5">
                {user?.contractInfo?.signed ? (
                  <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-green-800">Contrato Firmado</p>
                      <p className="text-sm text-green-600">
                        Firmado por: {user.contractInfo.fullName} - {new Date(user.contractInfo.signedAt).toLocaleDateString('es-CO')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <FileText className="w-6 h-6 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800">Contrato Pendiente</p>
                      <p className="text-sm text-amber-600">El referidor aún no ha firmado el contrato digital</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {formData.role === 'referidor' && !isNew && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="referidor-documents">
              <div className="p-5 border-b border-slate-100">
                <h2 className="font-semibold text-slate-900 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" /> Documentos del Referidor
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  {Object.keys(documents).length} de {documentTypes.length} documentos subidos
                </p>
              </div>
              <div className="p-5 space-y-4">
                {/* Progress */}
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${(Object.keys(documents).length / documentTypes.length) * 100}%`,
                      backgroundColor: primaryColor
                    }}
                  ></div>
                </div>

                {documentTypes.map((docType) => {
                  const doc = documents[docType.key];
                  const isUploading = uploading[docType.key];

                  return (
                    <div
                      key={docType.key}
                      className={`rounded-xl border p-4 ${doc ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${doc ? 'bg-green-100' : 'bg-slate-200'}`}>
                            {doc ? <CheckCircle className="w-5 h-5 text-green-600" /> : <FileText className="w-5 h-5 text-slate-400" />}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{docType.label}</p>
                            <p className="text-xs text-slate-500">{docType.description}</p>
                            {doc && (
                              <p className="text-xs text-green-600 mt-1">
                                Subido: {doc.name || 'documento'} ({new Date(doc.uploadedAt).toLocaleDateString('es-CO')})
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc && doc.url && (
                            <a
                              href={doc.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"
                              title="Descargar"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                          {doc && (
                            <button
                              onClick={() => handleDocumentDelete(docType.key)}
                              className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          <label className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg cursor-pointer transition-colors ${
                            isUploading ? 'bg-slate-100 text-slate-400' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
                          }`}>
                            {isUploading ? (
                              <div className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Upload className="w-3.5 h-3.5" />
                            )}
                            {doc ? 'Reemplazar' : 'Subir'}
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              disabled={isUploading}
                              onChange={(e) => {
                                if (e.target.files?.[0]) handleDocumentUpload(docType.key, e.target.files[0]);
                              }}
                            />
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Role & Status Card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-5 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900 text-sm">Rol y Estado</h2>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rol</label>
                <select
                  value={formData.role}
                  onChange={(e) => handleRoleChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                  data-testid="user-role-select"
                >
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Estado</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
                >
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>
              {(formData.role === 'comercial' || formData.role === 'admin' || formData.role === 'referidor') && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Comision (%)</label>
                  <input
                    type="number"
                    value={formData.commissionPercentage}
                    onChange={(e) => setFormData({ ...formData, commissionPercentage: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min="0" max="100" step="0.5"
                  />
                </div>
              )}
              {formData.role === 'asistente' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Etiquetas Asignadas</label>
                  <p className="text-xs text-slate-400 mb-2">Este usuario solo verá leads con estas etiquetas</p>
                  <div className="space-y-2">
                    {availableTags.length === 0 ? (
                      <p className="text-xs text-slate-400">No hay etiquetas creadas. Créalas en Configuración General.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {availableTags.map(tag => {
                          const isSelected = (formData.assignedTags || []).includes(tag.id);
                          return (
                            <button key={tag.id} type="button"
                              onClick={() => {
                                const current = formData.assignedTags || [];
                                const next = isSelected ? current.filter(t => t !== tag.id) : [...current, tag.id];
                                setFormData({ ...formData, assignedTags: next });
                              }}
                              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border-2 transition-all ${isSelected ? 'text-white border-transparent' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                              style={isSelected ? { backgroundColor: tag.color, borderColor: tag.color } : {}}
                              data-testid={`assign-tag-${tag.id}`}
                            >
                              {tag.name}
                              {isSelected && <Check className="w-3 h-3" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Referidor Rating Card */}
          {formData.role === 'referidor' && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="p-5 border-b border-slate-100">
                <h2 className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-500" /> Calificacion del Referidor
                </h2>
              </div>
              <div className="p-5 flex justify-center">
                <StarRating
                  rating={formData.referidorRating || 0}
                  onRate={(rating) => {
                    setFormData(prev => ({ ...prev, referidorRating: rating }));
                    if (!isNew) {
                      api.updateReferidorRating(userId, rating)
                        .then(() => toast.success('Calificacion actualizada'))
                        .catch(() => toast.error('Error al actualizar calificacion'));
                    }
                  }}
                  size="lg"
                />
              </div>
            </div>
          )}

          {/* Avatar Card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-5 border-b border-slate-100">
              <h2 className="font-semibold text-slate-900 text-sm">Avatar</h2>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-center">
                <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center text-3xl font-bold text-slate-500 border-2 border-dashed border-slate-300">
                  {formData.avatar ? (
                    <img src={formData.avatar} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    formData.name?.charAt(0)?.toUpperCase() || '?'
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
